import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon,
  PaperAirplaneIcon,
  EllipsisHorizontalIcon,
  PhotoIcon,
  ClockIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchMessages, sendMessage, markAsRead } from '../store/slices/messagesSlice';
import { formatDistanceToNow, isAfter, differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import Button from '../components/common/Button';
import Avatar from '../components/common/Avatar';

const ChatPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { messages, currentConversation, loading } = useAppSelector((state) => state.messages);
  const [timeLeft, setTimeLeft] = useState<string | null>(null);

  const isCurrentClosed = !!currentConversation?.closing_at || currentConversation?.is_closed;
  // Omit !
  const isCurrentExpired = currentConversation?.is_closed || (currentConversation?.closing_at && isAfter(new Date(), new Date(currentConversation.closing_at!)));

  useEffect(() => {
    if (currentConversation?.closing_at) {
      const calculateTimeLeft = () => {
        const now = new Date();
        const end = new Date(currentConversation.closing_at!);
        if (isAfter(now, end)) {
          setTimeLeft('Closed');
          return true;
        }

        const days = differenceInDays(end, now);
        const hours = differenceInHours(end, now) % 24;
        const mins = differenceInMinutes(end, now) % 60;
        setTimeLeft(`${days}d ${hours}h ${mins}m`);
        return false;
      };

      const isExpired = calculateTimeLeft();
      if (isExpired) return;

      const timer = setInterval(() => {
        if (calculateTimeLeft()) {
          clearInterval(timer);
        }
      }, 60000);

      return () => clearInterval(timer);
    } else {
      setTimeLeft(null);
    }
  }, [currentConversation?.closing_at]);

  // console.log("dsdsdsdsd", messages)
  useEffect(() => {
    if (conversationId) {
      dispatch(fetchMessages({ conversation_id: conversationId, page: 1 }));
      // console.log("conn",)
      dispatch(markAsRead(conversationId));
    }
  }, [dispatch, conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId || !currentConversation) return;

    const messageData = {
      recipient_id: currentConversation.other_user.id,
      content: newMessage.trim(),
      messageType: 'text',
    };
    // recipient_id: string; content: string; messageType?: string 

    await dispatch(sendMessage(messageData));
    setNewMessage('');
  };

  if (!currentConversation) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-x-0 bottom-0 md:left-64 md:w-[calc(100vw-16rem)] flex flex-col bg-white z-30 overflow-hidden"
      style={{ top: 'calc(4rem + max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 15px)' }}
    >
      {/* Chat Header */}
      <div className={`flex-none border-b border-gray-200 px-6 py-4 z-30 ${
        isCurrentClosed ? 'bg-gradient-to-r from-orange-400/10 to-amber-500/10 dark:from-orange-500/20 dark:to-amber-500/20 border-orange-200/50' : 'bg-white'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/messages"
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            
            <div className="flex items-center space-x-3">
              <Avatar
                src={currentConversation.other_user.profile_pic_thumb_signed || currentConversation.other_user.profile_pic}
                alt={`${currentConversation.other_user.first_name} ${currentConversation.other_user.last_name}`}
                size="md"
                className="w-10 h-10"
                isDeleted={!!currentConversation.other_user.deleted_at || !!currentConversation.other_user.deletion_scheduled_at}
              />
              
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-1.5 dark:text-gray-100">
                  <span className="truncate">{currentConversation.other_user.first_name} {currentConversation.other_user.last_name} ({messages.length})</span>
                  {isCurrentClosed && (
                    <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-sm shadow-orange-500/20 whitespace-nowrap flex-shrink-0">
                      Completed
                    </span>
                  )}
                </h2>
                <p className="text-sm text-gray-500 capitalize">
                  {currentConversation.other_user.user_type} • Online
                </p>
              </div>
            </div>
          </div>

          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <EllipsisHorizontalIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      {timeLeft && !isCurrentExpired && (
        <div className="bg-amber-50 dark:bg-amber-900/20 px-6 py-2 border-b border-amber-100 dark:border-amber-900/30 flex items-center justify-between text-amber-800 dark:text-amber-200 text-xs font-medium flex-none">
          <div className="flex items-center">
            <ClockIcon className="w-4 h-4 mr-2 text-amber-600 dark:text-amber-500 animate-pulse" />
            Chat expires in {timeLeft}.
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 pb-24 space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          messages.map((message, index) => {
            const isOwnMessage = message.sender_id === user?.id;
            const showAvatar = index === 0 || messages[index - 1].sender_id !== message.sender_id;
            
            return (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} max-w-[85%] md:max-w-[70%]`}>
                  {/* Avatar */}
                  {showAvatar && !isOwnMessage && (
                    <div className="flex-shrink-0 mr-3">
                      <Avatar
                        src={message.sender.profile_pic_thumb_signed || message.sender.profile_pic}
                        alt={`${message.sender.first_name} ${message.sender.last_name}`}
                        size="sm"
                        className="w-8 h-8"
                        isDeleted={!!message.sender.deleted_at || !!message.sender.deletion_scheduled_at}
                      />
                    </div>
                  )}
                  
                  {/* Message Bubble */}
                  <div className={`px-4 py-2 rounded-2xl ${
                    isOwnMessage
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-200 text-gray-900'
                  }`}>
                    <p className="text-sm break-words whitespace-pre-wrap">{message.content}</p>
                    <p className={`text-xs mt-1 ${
                      isOwnMessage ? 'text-primary-100' : 'text-gray-500'
                    }`}>
                      {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="flex-none bg-white border-t border-gray-200 p-4 pb-safe z-40">
        {!isCurrentExpired ? (
          <form onSubmit={handleSendMessage} className="flex items-end space-x-3">
            <button
              type="button"
              className="p-3 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <PhotoIcon className="h-6 w-6" />
            </button>
            
            <div className="flex-1">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                disabled={currentConversation?.is_blocked_by_me || currentConversation?.is_blocked_by_them}
                placeholder={currentConversation?.is_blocked_by_me || currentConversation?.is_blocked_by_them ? "Messaging unavailable" : "Type a message..."}
                className="w-full border border-gray-300 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                rows={1}
              />
            </div>
            
            <Button
              type="submit"
              variant="primary"
              disabled={!newMessage.trim() || currentConversation?.is_blocked_by_me || currentConversation?.is_blocked_by_them}
              className="p-3"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </Button>
          </form>
        ) : (
          <div className="flex items-center justify-center p-4 bg-gray-50 rounded-2xl border border-gray-200 text-sm text-gray-500 font-medium">
            <LockClosedIcon className="h-4 w-4 mr-2 text-gray-400" />
            This conversation is closed and view-only.
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
